import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { ApiKeyEventOption } from '@app/v2/settings/components/api-keys/api-key-card/api-key-card.component'
import { API_TOKEN_EVENT_SCOPES, API_TOKEN_REGISTRATION_SCOPES } from '@app/v2/settings/constants/api-token-scopes.constant'
import { ApiToken, CreateApiToken, UpdateApiToken } from '@app/v2/settings/enums/api-tokens.model'
import { WebhookIcons } from '@app/v2/settings/models/organization-settings.model'
import { ApiKeyService } from '@app/v2/settings/services/api-key.service'
import { ButtonSpinnerDirective } from '@app/v2/shared/button-loader.directive'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { DatePickerComponent } from '@app/v2/shared/components/inputs/date-picker/date-picker.component'
import { SelectInputComponent } from '@app/v2/shared/components/inputs/select-input/select-input.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { finalize } from 'rxjs'

@Component({
  selector: 'create-api-key-modal',
  standalone: true,
  imports: [ModalComponent, BasicInputComponent, SelectInputComponent, DatePickerComponent, ReactiveFormsModule, TranslateModule, ButtonSpinnerDirective],
  templateUrl: './create-api-key-modal.component.html'
})
export class CreateApiKeyModalComponent implements OnInit {
  @Input() id: string = 'create-api-key-modal'
  @Input() events: ApiKeyEventOption[] = []
  @Input() isLoadingEvents = false
  @Input() apiTokenId: number | null = null
  @Output() apiKeyCreated = new EventEmitter<{ token: string; name: string }>()
  @Output() apiKeyUpdated = new EventEmitter<ApiToken | null>()
  @Output() cancelled = new EventEmitter<void>()

  public readonly registrationScopes = API_TOKEN_REGISTRATION_SCOPES
  public readonly eventScopes = API_TOKEN_EVENT_SCOPES
  public readonly isSubmitting = signal(false)
  public readonly isLoadingApiToken = signal(false)
  public readonly minDate = new Date()
  public readonly plusIcon = WebhookIcons.getIcon().plusIcon
  public readonly editIcon = WebhookIcons.getIcon().editIcon

  public form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [this._requiredNonWhitespace] }),
    access_type: new FormControl<'full' | 'limited'>('full', { nonNullable: true }),
    event_id: new FormControl<number | null>(null),
    registration_scopes: new FormControl<string[]>([], { nonNullable: true, validators: [this._minArrayLength(1)] }),
    event_scopes: new FormControl<string[]>([], { nonNullable: true }),
    expires_at: new FormControl('', { nonNullable: true, validators: [Validators.required] })
  })

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _authService = inject(AuthService)
  private readonly _apiKeyService = inject(ApiKeyService)
  private readonly _alertService = inject(AlertService)
  private readonly _translate = inject(TranslateService)
  private _apiToken: ApiToken | null = null

  ngOnInit(): void {
    if (this.apiTokenId != null) this._loadApiToken(this.apiTokenId)
    else this._resetForm()

    this.form.controls.access_type.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((accessType) => this._syncEventValidators(accessType))
  }

  public isEditMode(): boolean {
    return this.apiTokenId != null
  }

  public isLimitedAccess(): boolean {
    return this.form.controls.access_type.value === 'limited'
  }

  public handleModalClose(): void {
    if (this.isSubmitting() || this.isLoadingApiToken()) return
    this._resetForm()
    this.cancelled.emit()
  }

  public onSave(): void {
    if (this.isSubmitting()) return

    this.form.markAllAsTouched()
    if (this.form.invalid) return

    if (this.isEditMode()) {
      this._updateApiKey()
      return
    }

    this._createApiKey()
  }

  public isSaveDisabled(): boolean {
    return this.isSubmitting() || this.isLoadingApiToken() || this.isLoadingEvents || this.form.invalid
  }

  private _createApiKey(): void {
    const organizationId = this._authService.currentWorkspace?.id
    if (!organizationId) return

    const payload: CreateApiToken = {
      organization_id: organizationId,
      ...this._formPayload()
    }

    if (this.form.controls.access_type.value === 'limited' && this.form.controls.event_id.value != null) {
      payload.event_id = this.form.controls.event_id.value
    }

    this.isSubmitting.set(true)
    this._apiKeyService
      .createApiKey(payload)
      .pipe(
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response) => {
          this.apiKeyCreated.emit({ token: response.token ?? '', name: payload.name })
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? this._translate.instant('v2.orgSettings.apiKeys.createApiKeyError'))
        }
      })
  }

  private _updateApiKey(): void {
    const token = this._apiToken
    if (!token) return

    const formPayload = this._formPayload()
    const payload: UpdateApiToken = {
      ...formPayload,
      event_id: this.form.controls.access_type.value === 'limited' && this.form.controls.event_id.value != null ? this.form.controls.event_id.value : ''
    }

    this.isSubmitting.set(true)
    this._apiKeyService
      .updateApiKey(token.id, payload)
      .pipe(
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (response) => {
          this.apiKeyUpdated.emit(response.data ?? null)
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? this._translate.instant('v2.orgSettings.apiKeys.updateError'))
        }
      })
  }

  private _formPayload(): Pick<CreateApiToken, 'name' | 'scopes' | 'expires_at'> {
    return {
      name: this.form.controls.name.value.trim(),
      scopes: {
        registrations: this.form.controls.registration_scopes.value as CreateApiToken['scopes']['registrations'],
        event: this.form.controls.event_scopes.value as CreateApiToken['scopes']['event']
      },
      expires_at: this._toExpiryDateTime(this.form.controls.expires_at.value)
    }
  }

  private _syncEventValidators(accessType: 'full' | 'limited'): void {
    const eventControl = this.form.controls.event_id

    if (accessType === 'limited') {
      eventControl.setValidators([Validators.required])
    } else {
      eventControl.clearValidators()
      eventControl.setValue(null, { emitEvent: false })
    }

    eventControl.updateValueAndValidity({ emitEvent: false })
  }

  private _loadApiToken(tokenId: number): void {
    this.isLoadingApiToken.set(true)
    this.form.disable({ emitEvent: false })

    this._apiKeyService
      .getApiKey(tokenId)
      .pipe(
        finalize(() => {
          this.isLoadingApiToken.set(false)
          if (!this._apiToken) return
          this.form.enable({ emitEvent: false })
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (token) => {
          this._apiToken = token
          this._prefillForm(token)
        },
        error: (error) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message ?? error?.message ?? this._translate.instant('v2.orgSettings.apiKeys.loadApiKeysError'))
          this.cancelled.emit()
        }
      })
  }

  private _prefillForm(token: ApiToken): void {
    const accessType = token.event_id == null ? 'full' : 'limited'

    this.form.reset(
      {
        name: token.name,
        access_type: accessType,
        event_id: token.event_id ?? null,
        registration_scopes: token.scopes?.registrations ?? [],
        event_scopes: token.scopes?.event ?? [],
        expires_at: this._toDateInput(token.expires_at)
      },
      { emitEvent: false }
    )
    this._syncEventValidators(accessType)
    this.form.markAsUntouched()
  }

  private _resetForm(): void {
    this._apiToken = null
    this.form.reset({
      name: '',
      access_type: 'full',
      event_id: null,
      registration_scopes: [],
      event_scopes: [],
      expires_at: ''
    })
    this._syncEventValidators('full')
    this.form.enable({ emitEvent: false })
    this.form.markAsUntouched()
  }

  private _toDateInput(value: string): string {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/)
    return match?.[1] ?? value
  }

  private _toExpiryDateTime(value: string): string {
    const date = this._toDateInput(value)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return value
    return `${date} 23:59:59`
  }

  private _requiredNonWhitespace(control: AbstractControl): ValidationErrors | null {
    const value = control.value
    if (typeof value !== 'string' || value.trim().length === 0) return { required: true }
    return null
  }

  private _minArrayLength(min: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value
      if (!Array.isArray(value) || value.length < min) return { minSelections: true }
      return null
    }
  }
}
